// HeroSlider.jsx
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useApp } from '../App';
import './HeroSlider.css';

const NOTIF_VISIBLE_MS = 5000;
const NOTIF_HIDDEN_MS  = 3000;
const NOTIF_PROGRESS_TICK_MS = 100;
const NOTIF_PROGRESS_STEP = 2;

/* Fallback slides if DB has none */
const FALLBACK_SLIDES = [
  {
    id: 'f1',
    image: 'https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=1920&h=800&fit=crop&q=85',
    title: 'Hospitality Professionals for Every Occasion',
    subtitle: 'Chefs, Housekeepers, Receptionists & Cleaners — all in one platform',
    primary_btn_label: 'Request a Professional',
    primary_btn_action: 'request',
    secondary_btn_label: 'Browse Professionals',
    secondary_btn_link: '/professionals'
  }
];

function HeroSlider() {
  const [slides, setSlides] = useState([]);
  const [current, setCurrent] = useState(0);
  const [progress, setProgress] = useState(0);

  const [showNotif, setShowNotif] = useState(false);
  const [notifProgress, setNotifProgress] = useState(0);
  const [notifDismissed, setNotifDismissed] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const { setShowRequest } = useApp();

  const showTimerRef = useRef(null);
  const hideTimerRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const remainingVisibleRef = useRef(NOTIF_VISIBLE_MS);
  const visibleStartTimeRef = useRef(null);
  const phaseRef = useRef('idle');

  /* ---------- LOAD SLIDES FROM API ---------- */
  useEffect(() => {
    axios.get('https://api.hospifylink.com/api/hero')
      .then(r => {
        if (Array.isArray(r.data) && r.data.length > 0) {
          setSlides(r.data);
        } else {
          setSlides(FALLBACK_SLIDES);
        }
      })
      .catch(() => setSlides(FALLBACK_SLIDES));
  }, []);

  /* ============================================
     HERO AUTO-SLIDE
     ============================================ */
  useEffect(() => {
    if (slides.length === 0) return;
    setProgress(0);
    const interval = setInterval(() => {
      setProgress(p => {
        if (p >= 100) {
          setCurrent(c => (c + 1) % slides.length);
          return 0;
        }
        return p + 2;
      });
    }, 100);
    return () => clearInterval(interval);
  }, [current, slides.length]);

  /* Reset current if slides shrink */
  useEffect(() => {
    if (current >= slides.length) setCurrent(0);
  }, [slides.length, current]);

  /* ============================================
     NOTIFICATION CYCLE (pausable on hover)
     ============================================ */
  useEffect(() => {
    if (notifDismissed) {
      setShowNotif(false);
      setNotifProgress(0);
      return;
    }

    const clearAllTimers = () => {
      if (showTimerRef.current) { clearTimeout(showTimerRef.current); showTimerRef.current = null; }
      if (hideTimerRef.current) { clearTimeout(hideTimerRef.current); hideTimerRef.current = null; }
      if (progressIntervalRef.current) { clearInterval(progressIntervalRef.current); progressIntervalRef.current = null; }
    };

    const enterHiddenPhase = () => {
      clearAllTimers();
      phaseRef.current = 'hidden';
      setShowNotif(false);
      setNotifProgress(0);
      remainingVisibleRef.current = NOTIF_VISIBLE_MS;

      showTimerRef.current = setTimeout(() => {
        if (!notifDismissed) enterVisiblePhase();
      }, NOTIF_HIDDEN_MS);
    };

    const enterVisiblePhase = () => {
      clearAllTimers();
      phaseRef.current = 'visible';
      setNotifProgress(0);
      setShowNotif(true);

      remainingVisibleRef.current = NOTIF_VISIBLE_MS;
      visibleStartTimeRef.current = Date.now();

      progressIntervalRef.current = setInterval(() => {
        if (isHovering) return;
        setNotifProgress(p => (p >= 100 ? 100 : p + NOTIF_PROGRESS_STEP));
      }, NOTIF_PROGRESS_TICK_MS);

      hideTimerRef.current = setTimeout(() => {
        if (isHovering) return;
        enterHiddenPhase();
      }, remainingVisibleRef.current);
    };

    showTimerRef.current = setTimeout(() => {
      if (!notifDismissed) enterVisiblePhase();
    }, 1200);

    return clearAllTimers;
  }, [notifDismissed]);

  /* ============================================
     HOVER HANDLERS
     ============================================ */
  useEffect(() => {
    if (phaseRef.current !== 'visible') return;

    if (isHovering) {
      if (hideTimerRef.current) { clearTimeout(hideTimerRef.current); hideTimerRef.current = null; }
      if (progressIntervalRef.current) { clearInterval(progressIntervalRef.current); progressIntervalRef.current = null; }
      if (visibleStartTimeRef.current) {
        const elapsed = Date.now() - visibleStartTimeRef.current;
        remainingVisibleRef.current = Math.max(0, remainingVisibleRef.current - elapsed);
      }
    } else {
      if (phaseRef.current !== 'visible') return;

      visibleStartTimeRef.current = Date.now();

      if (!progressIntervalRef.current) {
        progressIntervalRef.current = setInterval(() => {
          setNotifProgress(p => (p >= 100 ? 100 : p + NOTIF_PROGRESS_STEP));
        }, NOTIF_PROGRESS_TICK_MS);
      }

      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      hideTimerRef.current = setTimeout(() => {
        phaseRef.current = 'hidden';
        setShowNotif(false);
        setNotifProgress(0);
        remainingVisibleRef.current = NOTIF_VISIBLE_MS;

        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }

        showTimerRef.current = setTimeout(() => {
          if (notifDismissed) return;
          phaseRef.current = 'visible';
          setNotifProgress(0);
          setShowNotif(true);
          remainingVisibleRef.current = NOTIF_VISIBLE_MS;
          visibleStartTimeRef.current = Date.now();

          progressIntervalRef.current = setInterval(() => {
            setNotifProgress(p => (p >= 100 ? 100 : p + NOTIF_PROGRESS_STEP));
          }, NOTIF_PROGRESS_TICK_MS);

          hideTimerRef.current = setTimeout(() => {
            phaseRef.current = 'hidden';
            setShowNotif(false);
            setNotifProgress(0);
            if (progressIntervalRef.current) {
              clearInterval(progressIntervalRef.current);
              progressIntervalRef.current = null;
            }
          }, remainingVisibleRef.current);
        }, NOTIF_HIDDEN_MS);
      }, remainingVisibleRef.current);
    }
  }, [isHovering]);

  /* ============================================
     HANDLERS
     ============================================ */
  const handleNotifClick = () => {
    setShowRequest(true);
    setNotifDismissed(true);
    setShowNotif(false);
  };

  const handleNotifClose = (e) => {
    e.stopPropagation();
    setNotifDismissed(true);
    setShowNotif(false);
  };

  /* Fire the primary button: opens Request modal or navigates */
  const handlePrimaryClick = (slide) => {
    if (slide.primary_btn_action === 'request' || !slide.primary_btn_action) {
      setShowRequest(true);
    } else if (slide.primary_btn_action?.startsWith('/')) {
      window.location.href = slide.primary_btn_action;
    }
  };

  if (slides.length === 0) {
    return <section className="hero-root hero-loading" />;
  }

  const activeSlide = slides[current] || slides[0];

  return (
    <section className="hero-root">
      {slides.map((s, i) => (
        <div
          key={s.id || i}
          className={`hero-slide ${i === current ? 'hero-active' : ''}`}
        >
          <img src={s.image} alt={s.title} className="hero-img-tag" />
          <div className="hero-overlay" />
        </div>
      ))}

      <div className="hero-content">
        <h1 className="hero-title fade-up">{activeSlide.title}</h1>
        <p className="hero-sub fade-up">{activeSlide.subtitle}</p>
        <div className="hero-btns fade-up">
          <button
            className="lca-btn-primary hero-btn"
            onClick={() => handlePrimaryClick(activeSlide)}
          >
            {activeSlide.primary_btn_label || 'Request a Professional'}
          </button>
          {activeSlide.secondary_btn_label && (
            <Link
              to={activeSlide.secondary_btn_link || '/professionals'}
              className="hero-btn-outline"
            >
              {activeSlide.secondary_btn_label}
            </Link>
          )}
        </div>
      </div>

      <div className="hero-dots">
        {slides.map((_, i) => (
          <button
            key={i}
            className={`hero-dot ${i === current ? 'hero-dot-active' : ''}`}
            onClick={() => setCurrent(i)}
          >
            {i === current && (
              <span
                className="hero-dot-progress"
                style={{ width: `${progress}%` }}
              />
            )}
          </button>
        ))}
      </div>

      <div
        className={`hero-notif ${showNotif ? 'hero-notif-show' : ''} ${isHovering ? 'hero-notif-paused' : ''}`}
        onClick={handleNotifClick}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        role="button"
        tabIndex={0}
        aria-label="Just make request"
      >
        <div className="hero-notif-icon">
          <i className="fas fa-bell" />
        </div>
        <div className="hero-notif-text">
          <span className="hero-notif-title">Just Make Request</span>
          <span className="hero-notif-sub">
            {isHovering ? 'Click to book Professional now' : 'Click to book now'}
          </span>
        </div>
        <div
          className="hero-notif-close"
          onClick={handleNotifClose}
          aria-label="Close notification"
          role="button"
          tabIndex={0}
        >
          <i className="fas fa-times" />
        </div>
        <div className="hero-notif-progress">
          <span style={{ width: `${notifProgress}%` }} />
        </div>
      </div>
    </section>
  );
}

export default HeroSlider;